<script setup lang="ts">
  import { ref, onMounted, inject } from 'vue'
  import {
    Clock,
    CircleCheck,
    Plus,
    CircleX,
    CircleArrowRight,
    ChevronRightIcon
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import {
    AppointmentsService,
    type CustomerCase as Case,
    type CustomerAppointment
  } from '@/types/generated'
  import { useToast } from '@/components/ui/toast'
  import { genericSuccessToast } from '@/lib/toasts'
  import CreateAppointmentModal from '@/components/app/cases/appointmentCreationModal.vue'
  import { useRouter } from 'vue-router'

  const router = useRouter()
  const store = useCaseStore()
  const { toast } = useToast()
  const showCreateModal = ref(false)
  const isLoading = ref(true)
  const upcomingAppointments = ref<CustomerAppointment[]>([])

  const actionLabel = 'Neuer Termin'
  const actionIcon = Plus
  const handleAction = () => {
    showCreateModal.value = true
  }
  defineExpose({
    actionLabel,
    actionIcon,
    handleAction
  })

  const MAX_DISPLAYED_APPOINTMENTS = 3

  onMounted(async () => {
    await loadAppointments()
  })

  const loadAppointments = async () => {
    try {
      isLoading.value = true

      // Filter to get only upcoming appointments and unconfirmed proposals
      const allAppointments = [...(store.caseRecord?.appointments || [])]
      const proposals = [...(store.caseRecord?.appointment_proposals || [])].filter(
        (prop) => !prop.booked
      )

      // Process appointments and proposals
      const processedAppointments = allAppointments.map((apt) => ({
        ...apt,
        isProposal: false
      }))

      const processedProposals = proposals.map((prop) => ({
        ...prop,
        end: getEndTime(prop.start, prop.service?.duration_mins),
        status: 'LINK_SENT',
        isProposal: true
      }))

      // Combine and sort by start date
      const combined = [...processedAppointments, ...processedProposals]
        // .filter((apt) => {
        //   // Only keep future appointments (today or later)
        //   const aptDate = new Date(apt.start)
        //   console.log('aptDate', aptDate)
        //   const today = new Date()
        //   today.setHours(0, 0, 0, 0)
        //   console.log('today', today)
        //   return aptDate >= today
        // })
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())

      // Limit to first few appointments
      upcomingAppointments.value = combined.slice(
        0,
        MAX_DISPLAYED_APPOINTMENTS
      ) as CustomerAppointment[]
    } catch (error) {
      console.error('Error loading appointments:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Termine konnten nicht geladen werden'
      })
    } finally {
      isLoading.value = false
    }
  }

  const getEndTime = (startTime: string, durationMins: number = 60) => {
    const startDate = new Date(startTime)
    const endDate = new Date(startDate.getTime() + durationMins * 60 * 1000)
    return endDate.toISOString()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatTime = (startDate: string) => {
    const start = new Date(startDate)
    return `${start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`
  }

  const getLocationText = (locationType: string) => {
    return locationType === 'ONLINE' ? 'Online' : 'Vor Ort'
  }

  const getFullDisplayDate = (appointment: any) => {
    return `${formatTime(appointment.start)}, ${formatDate(appointment.start)}, ${getLocationText(appointment.location_types)}`
  }

  const getInitials = (name: string) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2)
  }

  // Handle appointment confirmation and rejection
  const confirmAppointment = async (appointment: any) => {
    try {
      await AppointmentsService.appointmentsConfirmCreate({
        uuid: appointment.uuid,
        requestBody: {
          ...appointment,
          status: 'CONFIRMED'
        }
      })

      await loadAppointments()
      genericSuccessToast()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Termin konnte nicht bestätigt werden'
      })
    }
  }

  const rejectAppointment = async (appointment: any) => {
    try {
      await AppointmentsService.appointmentsCancelCreate({
        requestBody: {
          ...appointment,
          status: 'CANCELLED'
        },
        uuid: appointment.uuid
      })

      await loadAppointments()
      genericSuccessToast()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Termin konnte nicht abgelehnt werden'
      })
    }
  }

  // Navigate to full appointments view
  const viewAllAppointments = () => {
    if (store.caseRecord?.uuid) {
      router.push(`/app/cases/${store.caseRecord.uuid}/appointments`)
    }
  }

  const handleAppointmentCreated = async () => {
    await loadAppointments()
  }

  const registerWidgetActionHandler = inject('registerWidgetActionHandler')
  onMounted(() => {
    if (registerWidgetActionHandler) {
      registerWidgetActionHandler('APPOINTMENTS', () => {
        showCreateModal.value = true
      })
    }
  })
</script>

<template>
  <div class="appointment-widget rounded-xl bg-white">
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="flex justify-center py-6"
    >
      <div
        class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
      ></div>
    </div>

    <!-- No Appointments State -->
    <div
      v-else-if="upcomingAppointments.length === 0"
      class="flex flex-col items-center justify-center rounded-lg border border-gray-300 px-4 py-10 shadow-sm"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          width="40"
          height="40"
          rx="8"
          fill="#F3E8FF"
        />
        <path
          d="M19 20H11M24 14H11M24 26H11M27 18L23 22M23 18L27 22"
          stroke="#7E22CE"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p class="text-md mt-5 text-center text-gray-500">Keine bevorstehenden Termine</p>
    </div>

    <!-- Appointments List -->
    <div
      v-else
      class="space-y-2"
    >
      <div
        v-for="appointment in upcomingAppointments"
        :key="appointment.uuid"
        class="rounded-lg border border-gray-200 bg-white p-3"
      >
        <!-- Appointment Header with Case Worker Info -->
        <div class="mb-2 flex items-center gap-3">
          <div class="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-white">
            {{ getInitials(appointment.calendar?.name) }}
          </div>
          <div>
            <div class="font-medium">
              {{ appointment.calendar?.name || 'Unbekannt' }}
              <span class="text-sm font-normal text-gray-500">
                {{ appointment.calendar?.email }}
              </span>
            </div>
          </div>
        </div>

        <!-- Content Area -->
        <div class="space-y-1">
          <!-- Title and Actions Row -->
          <div class="flex items-center justify-between">
            <!-- Appointment Title -->
            <h3 class="text-base font-medium text-gray-900">
              {{ appointment.summary || appointment.service?.name || 'Unbekannter Termin' }}
            </h3>

            <div
              v-if="appointment.status === 'TENTATIVE'"
              class="flex gap-2"
            >
              <Button
                variant="default"
                class="h-8 bg-green-600 px-3 py-1 text-white hover:bg-green-700"
                @click="confirmAppointment(appointment)"
              >
                Bestätigen
              </Button>
              <Button
                variant="secondary"
                size="sm"
                class="h-8 px-3 py-1 text-red-600"
                @click="rejectAppointment(appointment)"
              >
                Ablehnen
              </Button>
            </div>

            <!-- Status Display Section-->
            <!-- <div
              v-else-if="
                appointment.status === 'CONFIRMED' && appointment.location_types === 'ONLINE'
              "
            >
              <Button class="h-8 bg-purple-600 px-3 py-1 text-white hover:bg-purple-700">
                <Video class="mr-1.5 h-4 w-4" />
                Teilnehmen
              </Button>
            </div> -->

            <div
              v-else-if="appointment.status === 'CONFIRMED'"
              class="flex items-center text-green-600"
            >
              <CircleCheck class="mr-1 h-5 w-5 fill-green-600 text-white" />
              Bestätigt
            </div>

            <div
              v-else-if="appointment.status === 'CANCELLED'"
              class="flex items-center text-red-600"
            >
              <CircleX class="mr-1 h-5 w-5 fill-red-600 text-white" />
              Abgelehnt
            </div>

            <div
              v-else-if="appointment.status === 'LINK_SENT'"
              class="flex items-center text-blue-600"
            >
              <CircleArrowRight class="mr-1 h-5 w-5 fill-blue-600 text-white" />
              Link gesendet
            </div>
          </div>

          <!-- Appointment Time -->
          <div class="flex items-center gap-1.5 text-sm text-gray-600">
            <Clock class="h-4 w-4" />
            <span>{{ getFullDisplayDate(appointment) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- View All Link -->
    <div class="items-left mt-4 flex justify-end">
      <a
        href="#"
        class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
        @click.prevent="viewAllAppointments"
      >
        Alle Terminen ansehen
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div>

    <!-- Create Appointment Modal -->
    <CreateAppointmentModal
      v-model:open="showCreateModal"
      :case="store.caseRecord"
      @appointment-created="handleAppointmentCreated"
    />
  </div>
</template>
